import { AU, CA, EU, US } from "country-flag-icons/react/3x2";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFilter } from "react-aria";
import {
  Button,
  Input,
  ListBox,
  ListBoxItem,
  MenuTrigger,
  Popover,
  SearchField,
} from "react-aria-components";
import { useLocation } from "react-router-dom";

const REGION_TO_FQDN = {
  dc8: "app.bland.ai",
  dc9: "ca.app.bland.ai",
  dc11: "eu.app.bland.ai",
  dc10: "asia.app.bland.ai",
} as const;

const REGION_NAMES = {
  dc8: "US West",
  dc9: "Canada",
  dc11: "Europe",
  dc10: "Asia",
} as const;

const REGION_FLAGS = {
  dc5: <US style={{ width: 16, height: 11, borderRadius: 2 }} />,
  dc8: <US style={{ width: 16, height: 11, borderRadius: 2 }} />,
  dc9: <CA style={{ width: 16, height: 11, borderRadius: 2 }} />,
  dc10: <AU style={{ width: 16, height: 11, borderRadius: 2 }} />,
  dc11: <EU style={{ width: 16, height: 11, borderRadius: 2 }} />,
} as const;

export default function RegionSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  const { contains } = useFilter({ sensitivity: "base" });

  const regions = useMemo(
    () =>
      Object.entries(REGION_TO_FQDN).map(([id, fqdn]) => ({
        id,
        fqdn,
        name: REGION_NAMES[id as keyof typeof REGION_NAMES],
        icon: REGION_FLAGS[id as keyof typeof REGION_FLAGS],
      })),
    [],
  );

  const filteredRegions = useMemo(() => {
    if (!searchQuery) return regions;
    return regions.filter((r) => contains(r.name, searchQuery));
  }, [regions, searchQuery, contains]);

  const currentRegion = useMemo(() => {
    return regions.find((r) => r.fqdn === window.location.hostname);
  }, [regions]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isOpen && searchInputRef.current) {
      timeout = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen]);

  return (
    <div>
      <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <Button
          className={`
            flex w-36 cursor-pointer items-center justify-between gap-2
            rounded-[10px] border-none p-2 text-left
            ${isOpen ? "bg-[#f5f5f5]" : "bg-transparent"}
          `}
        >
          {currentRegion ? (
            <div className="flex items-center gap-2">
              {currentRegion.icon}
              <span className="text-sm font-medium">{currentRegion.name}</span>
            </div>
          ) : (
            <span className="text-sm font-medium">Select Region</span>
          )}
          <ChevronsUpDown size={16} />
        </Button>

        <Popover
          placement="top start"
          className={`
            min-w-[260px] rounded-md border border-dark-secondary
            bg-dark-primary p-2 shadow
          `}
        >
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            aria-label="Search regions"
          >
            <div className="relative flex h-10 items-center">
              <Search
                size={13}
                className={`
                  absolute top-1/2 left-3 -translate-y-1/2
                  text-foreground-inverse-secondary
                `}
              />
              <Input
                ref={searchInputRef}
                placeholder="Find a region..."
                className={`
                  h-10 w-full bg-transparent pr-2 pl-8 text-sm
                  text-foreground-inverse outline-none
                  placeholder:text-foreground-inverse-secondary
                `}
              />
            </div>
          </SearchField>

          <ListBox
            className="mt-2 max-h-[220px] overflow-y-auto outline-none"
            items={filteredRegions}
            selectionMode="single"
            aria-label="Region list"
            onSelectionChange={(keys) => {
              const selectedId = Array.from(keys)[0];
              if (selectedId) {
                const fqdn =
                  REGION_TO_FQDN[selectedId as keyof typeof REGION_TO_FQDN];
                if (fqdn) {
                  window.location.replace(
                    `https://${fqdn}${location.pathname}`,
                  );
                }
              }
            }}
          >
            {(item) => (
              <ListBoxItem
                key={item.id}
                id={item.id}
                className={`
                  flex cursor-pointer items-center gap-3 rounded px-2 py-2
                  text-sm text-foreground-inverse
                  selected:bg-dark-tertiary
                  hover:bg-hover-dark-16
                `}
              >
                {item.icon}
                <div className="flex flex-col">
                  <span className="font-medium">{item.name}</span>
                  <span className="text-xs text-gray-500">{item.fqdn}</span>
                </div>
                {window.location.hostname === item.fqdn && (
                  <Check
                    size={14}
                    className="ml-auto text-foreground-inverse"
                  />
                )}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </MenuTrigger>
    </div>
  );
}
